import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Mail, Phone, Calendar, User, MessageSquare, RefreshCw, CheckCircle, Clock, XCircle, ExternalLink, Trash2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import api from '../../lib/api';

const ContactSubmissionsTab = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/contact-submissions');
      setSubmissions(res.data);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (submissionId, newStatus) => {
    try {
      setUpdating(submissionId);
      await api.patch(`/contact-submissions/${submissionId}/status?status=${newStatus}`);
      setSubmissions(prev => 
        prev.map(s => s.id === submissionId ? { ...s, status: newStatus } : s)
      );
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const deleteSubmission = async (submissionId, parentName) => {
    if (!window.confirm(`Are you sure you want to delete the enquiry from ${parentName}? This cannot be undone.`)) {
      return;
    }
    
    try {
      setDeleting(submissionId);
      await api.delete(`/contact-submissions/${submissionId}`);
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      toast.success('Enquiry deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete enquiry');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      sent: { color: 'bg-blue-500/20 text-blue-400', icon: Mail },
      contacted: { color: 'bg-purple-500/20 text-purple-400', icon: Phone },
      converted: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      closed: { color: 'bg-gray-500/20 text-gray-400', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status || 'pending'}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contact-submissions-tab">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Contact Submissions</h2>
          <p className="text-white/60 text-sm mt-1">
            {submissions.length} total enquiries
          </p>
        </div>
        <Button 
          onClick={fetchSubmissions} 
          variant="outline" 
          className="border-[#333] text-white hover:bg-white/10"
          data-testid="refresh-submissions"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Empty State */}
      {submissions.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Submissions Yet</h3>
            <p className="text-white/50">Contact form submissions will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <Card key={submission.id} className="bg-[#111] border-[#333]" data-testid={`submission-${submission.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  {/* Main Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-white flex items-center gap-2">
                          <User className="w-4 h-4 text-[#FF007F]" />
                          {submission.parent_name || 'Unknown'}
                        </h3>
                        <p className="text-white/60 text-sm mt-1">
                          Student: <span className="text-white">{submission.student_name || 'Not specified'}</span>
                        </p>
                      </div>
                      {getStatusBadge(submission.status)}
                    </div>

                    {/* Contact Details */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <a 
                        href={`mailto:${submission.email}`} 
                        className="flex items-center gap-1 text-[#00D4FF] hover:underline"
                        data-testid={`email-${submission.id}`}
                      >
                        <Mail className="w-3 h-3" />
                        {submission.email}
                      </a>
                      <a 
                        href={`tel:${submission.phone}`} 
                        className="flex items-center gap-1 text-white/70 hover:text-white"
                      >
                        <Phone className="w-3 h-3" />
                        {submission.phone}
                      </a>
                      <span className="flex items-center gap-1 text-white/50">
                        <Calendar className="w-3 h-3" />
                        {formatDate(submission.created_at)}
                      </span>
                    </div>

                    {/* Interest & Message */}
                    <div className="space-y-2">
                      <Badge className="bg-[#FF007F]/20 text-[#FF007F]">
                        {submission.student_type === 'primatotterina' ? 'Primatotterina (Ages 2-5)' : 'UPA (Ages 6-18)'}
                      </Badge>
                      
                      {submission.message && (
                        <div className="bg-[#1a1a1a] rounded-lg p-3 mt-2">
                          <p className="text-white/70 text-sm whitespace-pre-wrap break-words">
                            {submission.message.length > 300 
                              ? `${submission.message.substring(0, 300)}...` 
                              : submission.message}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Update Status</p>
                    {['contacted', 'converted', 'closed'].map((status) => (
                      <Button
                        key={status}
                        size="sm"
                        variant={submission.status === status ? 'default' : 'outline'}
                        className={
                          submission.status === status 
                            ? 'bg-[#FF007F] hover:bg-[#D6006A]' 
                            : 'border-[#333] text-white/70 hover:bg-white/10'
                        }
                        disabled={updating === submission.id || submission.status === status}
                        onClick={() => updateStatus(submission.id, status)}
                        data-testid={`status-${status}-${submission.id}`}
                      >
                        {updating === submission.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          status.charAt(0).toUpperCase() + status.slice(1)
                        )}
                      </Button>
                    ))}
                    
                    {/* Quick Reply Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#00D4FF] text-[#00D4FF] hover:bg-[#00D4FF]/10 mt-2"
                      onClick={() => window.open(`mailto:${submission.email}?subject=Re: Your Enquiry about ${submission.student_name}`, '_blank')}
                      data-testid={`reply-${submission.id}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Reply
                    </Button>
                    
                    {/* Delete Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10 mt-1"
                      disabled={deleting === submission.id}
                      onClick={() => deleteSubmission(submission.id, submission.parent_name)}
                      data-testid={`delete-${submission.id}`}
                    >
                      {deleting === submission.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactSubmissionsTab;
